import { SectionHeading } from "@/components/shared/SectionHeading";
import { Reveal, RevealStagger, RevealItem } from "@/components/shared/Reveal";
import { Caravan, ShieldAlert, ArrowRight, CheckCircle2 } from "lucide-react";
import { openPreRegister } from "@/lib/preRegisterModal";

const RESCUE_INFO = ["Vehicle location", "Destination", "VIN", "Make/model/year", "RV class", "Vehicle condition", "Keys status", "Photographs"];
const DELIVERY_USES = ["Dealer to customer delivery", "Dealer to dealer transfer", "Fleet repositioning", "Rental recovery", "Vehicle relocation"];
const RV_VERIFICATION = ["Identity", "VIN", "Registration", "Ownership documentation", "Agency authorization", "Vehicle photographs"];

export const RVRescue = () => {
  return (
    <section id="rv" data-testid="rv-rescue-section" className="py-24 md:py-32 bg-rr-navy relative overflow-hidden">
      <div className="grain-overlay" />
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <SectionHeading
          eyebrow="RV Rescue & Delivery"
          title="Need Your RV Brought Home? Need a Sold RV Delivered?"
          description="RelayRigs connects authorized RV transportation requests with appropriately qualified drivers."
          light
          testId="rv-rescue"
        />

        <div className="mt-14 grid lg:grid-cols-2 gap-6">
          <Reveal>
            <div className="relative rounded-[2rem] overflow-hidden h-full min-h-[420px] group">
              <img src="https://images.unsplash.com/photo-1655827268198-aee7f7ace729?crop=entropy&cs=srgb&fm=jpg&q=85" alt="RV on the road" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-rr-navyDeep via-rr-navyDeep/40 to-transparent" />
              <div className="relative p-8 flex flex-col h-full min-h-[420px] justify-end">
                <Caravan className="text-rr-orangeLight mb-3" size={28} />
                <h3 className="font-heading text-2xl font-bold text-white">RV Rescue</h3>
                <p className="text-white/70 text-sm mt-2 max-w-sm">For RV owners or authorized parties who need an RV recovered or relocated — left in another state, stranded but drivable, or repositioned.</p>
                <div className="flex flex-wrap gap-2 mt-4">
                  {RESCUE_INFO.map((r) => (
                    <span key={r} data-testid={`rv-rescue-info-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="text-[11px] font-semibold bg-white/10 text-white px-2.5 py-1 rounded-full">{r}</span>
                  ))}
                </div>
                <p className="text-xs text-rr-orangeLight mt-4">RelayRigs does not dispatch a drive-away driver when a vehicle requires towing.</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="rounded-[2rem] p-8 h-full min-h-[420px] bg-white/[0.04] border border-white/10 flex flex-col justify-between">
              <div>
                <ShieldAlert className="text-rr-orangeLight mb-3" size={28} />
                <h3 className="font-heading text-2xl font-bold text-white">RV Delivery</h3>
                <p className="text-white/70 text-sm mt-2 max-w-sm">For RV dealers, rental fleets, RV fleets, owners and authorized businesses.</p>
                <RevealStagger className="mt-6 space-y-2.5">
                  {DELIVERY_USES.map((u) => (
                    <RevealItem key={u}>
                      <div data-testid={`rv-delivery-use-${u.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="flex items-center gap-2 text-sm font-semibold text-white/80">
                        <CheckCircle2 size={15} className="text-rr-green shrink-0" /> {u}
                      </div>
                    </RevealItem>
                  ))}
                </RevealStagger>
              </div>
              <div>
                <button onClick={() => openPreRegister("rv_service")} data-testid="request-rv-transportation-cta" className="w-full mt-8 inline-flex items-center justify-center gap-2 bg-rr-orange text-white font-bold px-7 py-4 rounded-full hover:bg-white hover:text-rr-navy transition-all hover:-translate-y-1">
                  Join the RV Service Waitlist <ArrowRight size={18} />
                </button>
                <button onClick={() => openPreRegister("rv_dealer")} data-testid="rv-dealer-early-access-cta" className="mt-3 block text-sm font-bold text-rr-orangeLight underline underline-offset-4 decoration-rr-orangeLight/40 hover:decoration-rr-orangeLight text-left">
                  RV Dealer / Fleet Early Access →
                </button>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.3}>
          <div className="mt-8 rounded-2xl border border-rr-orangeLight/20 bg-white/[0.03] p-6 flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="flex items-center gap-2 text-rr-orangeLight font-bold text-sm shrink-0">
              <ShieldAlert size={18} /> Authorized Recovery Only.
            </div>
            <p className="text-white/50 text-sm">
              RelayRigs requires appropriate ownership or authorization information before sensitive recovery information or vehicle custody is provided: {RV_VERIFICATION.join(", ")}.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
